/* Atelier Noir — product add-to-cart (progressive enhancement).
   <product-form> intercepts submit, adds to cart via fetch, re-renders the
   cart drawer, and flashes state. <sticky-atc> shows a compact mobile bar
   once the main button scrolls out of view. No JS => the form POSTs natively. */

const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

class ProductForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.button = this.querySelector('[data-atc]');
    this.textEl = this.querySelector('[data-atc-text]');
    this.errorEl = this.querySelector('[data-form-error]');
    this.form.addEventListener('submit', this.onSubmit.bind(this));
  }

  async onSubmit(event) {
    if (!window.fetch) return; // let the browser POST natively
    event.preventDefault();
    if (!this.button || this.button.getAttribute('aria-disabled') === 'true') return;

    this.hideError();
    this.setBusy(true);

    try {
      const body = new FormData(this.form);
      body.append('sections', 'cart-drawer');
      const res = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });
      const json = await res.json();

      if (!res.ok) {
        this.showError(json.description || json.message);
        this.setBusy(false);
        return;
      }

      // /cart/add.js returns the added line item, not the cart — fetch the
      // real cart so listeners (header count, drawer) get item_count etc.
      let cart = null;
      try {
        cart = await (await fetch(`${root()}cart.js`, { headers: { Accept: 'application/json' } })).json();
      } catch (e) {
        cart = json;
      }
      document.dispatchEvent(
        new CustomEvent('cart:updated', { detail: { cart, sections: json.sections } })
      );
      document.dispatchEvent(new CustomEvent('cart:open'));
      this.flashAdded();
    } catch (e) {
      this.showError();
      this.setBusy(false);
    }
  }

  setBusy(busy) {
    if (!this.button) return;
    this.button.disabled = busy;
    if (this.textEl && busy) this.textEl.textContent = this.button.dataset.labelAdding;
  }

  flashAdded() {
    if (this.textEl) this.textEl.textContent = this.button.dataset.labelAdded;
    setTimeout(() => {
      if (this.textEl) this.textEl.textContent = this.button.dataset.labelDefault;
      this.button.disabled = false;
    }, 1200);
  }

  showError(message) {
    if (!this.errorEl) return;
    this.errorEl.textContent = message || this.dataset.errorGeneric || '';
    this.errorEl.hidden = false;
  }

  hideError() {
    if (this.errorEl) this.errorEl.hidden = true;
  }
}

class StickyAtc extends HTMLElement {
  connectedCallback() {
    if (!('IntersectionObserver' in window)) return;
    const form = document.getElementById(this.dataset.target);
    if (!form) return;
    const watch = form.querySelector('[data-atc]') || form;
    this.button = this.querySelector('[data-sticky-atc]');

    this.hidden = false; // JS takes over visibility via transform

    this.io = new IntersectionObserver(
      (entries) => {
        const e = entries[0];
        this.toggle(!e.isIntersecting && e.boundingClientRect.top < 0);
      },
      { threshold: 0 }
    );
    this.io.observe(watch);

    if (this.button) {
      this.button.addEventListener('click', () => {
        if (form.requestSubmit) form.requestSubmit();
        else form.submit();
      });
    }
  }

  toggle(show) {
    if (show === this._shown) return;
    this._shown = show;
    this.classList.toggle('is-visible', show);
    this.setAttribute('aria-hidden', show ? 'false' : 'true');
  }

  disconnectedCallback() {
    if (this.io) this.io.disconnect();
  }
}

if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);
if (!customElements.get('sticky-atc')) customElements.define('sticky-atc', StickyAtc);
